import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import { fetchReviewQueue } from '../lib/api';

/**
 * Small "N pending review" badge that links to `/review`.
 *
 * Per docs/contributor-capture-and-review-spec.md §8 (Notification —
 * V1 uses in-app badges; email digest is V1.5; web push is V2).
 *
 * Polls the queue manifest on mount + every `pollIntervalMs` (default
 * 60 sec). The endpoint is auth-gated; a 401 means "user not signed
 * in" which is fine — badge stays hidden in that case.
 *
 * Hidden when pending === 0 (don't clutter the chrome with a zero
 * badge). Renders inline so callers can drop it into any nav row.
 */

interface Props {
  /** ms between polls. Default 60_000 (1 min). 0 disables polling. */
  pollIntervalMs?: number;
  /** Optional class for the wrapping <Link>. */
  className?: string;
}

export function QueueBadge({ pollIntervalMs = 60_000, className }: Props) {
  const [pending, setPending] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function poll(): Promise<void> {
      try {
        const m = await fetchReviewQueue();
        if (!cancelled) setPending(m.pending);
      } catch {
        // 401 (unauthenticated) or network — leave badge hidden.
        if (!cancelled) setPending(null);
      }
    }
    void poll();
    if (pollIntervalMs <= 0) return;
    const id = window.setInterval(() => void poll(), pollIntervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [pollIntervalMs]);

  if (pending === null || pending === 0) return null;

  return (
    <Link
      href="/review"
      class={['queue-badge', className].filter(Boolean).join(' ')}
      aria-label={`${pending} photo${pending === 1 ? '' : 's'} pending review`}
    >
      <span class="queue-badge__count">{pending}</span>
      <span class="queue-badge__label">
        {pending === 1 ? 'photo to review' : 'photos to review'}
      </span>
    </Link>
  );
}
